// The host's game state, packed small enough to send 60 times a second.
//
// A guest only needs what the renderer and the HUD read: the stack (whose
// block is where), each falling piece, each next piece, and the totals. The
// stack goes as one string - one character per cell, the owner as a
// letter ('a' is seat 0 ... 'l' is seat 11) or '.' - which is about 500
// bytes for a four-player well instead of the ~15 KB the raw object would
// be. (Letters, not digits: seat 10 would need two characters.)

import { ROTATIONS } from '../engine/pieces.js';

export function packGame(state) {
  return {
    w: state.width,
    h: state.height,
    g: state.grid.map((row) => row.map((c) => (c ? String.fromCharCode(97 + c.owner) : '.')).join('')).join(''),
    p: state.players.map((p) => [p.piece ? [p.piece.shape, p.piece.rot, p.piece.x, p.piece.y] : 0, p.queue[0], p.lines, p.cleared]),
    s: state.score,
    l: state.lines,
    v: state.level,
    o: state.over ? 1 : 0,
    z: state.paused ? 1 : 0,
  };
}

// Rebuilds something shaped enough like the engine's state for draw(),
// ghostOf() and the HUD. Stack cells carry no shape - nothing that draws a
// locked block needs it.
export function unpackGame(snap) {
  const grid = [];
  for (let y = 0; y < snap.h; y += 1) {
    const row = [];
    for (let x = 0; x < snap.w; x += 1) {
      const ch = snap.g[y * snap.w + x];
      row.push(ch === '.' ? null : { owner: ch.charCodeAt(0) - 97 });
    }
    grid.push(row);
  }
  return {
    width: snap.w,
    height: snap.h,
    grid,
    players: snap.p.map(([piece, next, lines, cleared], id) => ({
      id,
      piece: piece ? { shape: piece[0], rot: piece[1], x: piece[2], y: piece[3] } : null,
      queue: [next],
      lines,
      cleared: cleared || 0,
    })),
    score: snap.s,
    lines: snap.l,
    level: snap.v,
    over: Boolean(snap.o),
    paused: Boolean(snap.z),
    events: [],
  };
}

// ---- party mode: one phone's window ------------------------------------
//
// A phone shows a slice of the well around its own piece, so that's all it
// is sent: `w` columns starting at `x0`, the falling pieces that overlap
// them (in the slice's own coordinates), and its own next piece. The
// owners stay global seat numbers, so colours line up with the TV.

export const PHONE_COLUMNS = 10;
// How close to the window's edge a piece may get before the window follows.
const EDGE = 3;

// Moves a phone's window just enough to keep its piece away from the edges.
export function followCamera(x0, state, me, w = PHONE_COLUMNS) {
  const width = Math.min(w, state.width);
  const piece = state.players[me]?.piece;
  let next = x0;
  if (piece) {
    const xs = ROTATIONS[piece.shape][piece.rot].map(([x]) => piece.x + x);
    const left = Math.min(...xs);
    const right = Math.max(...xs);
    if (left - next < EDGE) next = left - EDGE;
    if (right - next > width - 1 - EDGE) next = right - (width - 1 - EDGE);
  }
  return Math.max(0, Math.min(state.width - width, Math.round(next)));
}

export function packWindow(state, me, x0, w = PHONE_COLUMNS) {
  const width = Math.min(w, state.width);
  const rows = state.grid.map((row) => row.slice(x0, x0 + width)
    .map((c) => (c ? String.fromCharCode(97 + c.owner) : '.')).join(''));
  const pieces = [];
  state.players.forEach((p, id) => {
    if (!p.piece) return;
    const xs = ROTATIONS[p.piece.shape][p.piece.rot].map(([x]) => p.piece.x + x);
    if (Math.max(...xs) < x0 || Math.min(...xs) >= x0 + width) return;
    pieces.push([id, p.piece.shape, p.piece.rot, p.piece.x - x0, p.piece.y]);
  });
  return {
    x0,
    W: state.width,
    w: width,
    h: state.height,
    n: state.players.length,
    me,
    g: rows.join(''),
    p: pieces,
    q: state.players[me]?.queue[0] || null,
    // This phone's own blocks in cleared lines and its place among everyone
    // (ties share a place), for its end-of-game card.
    c: state.players[me]?.cleared || 0,
    r: placeOf(state.players, me),
    s: state.score,
    l: state.lines,
    v: state.level,
    o: state.over ? 1 : 0,
    z: state.paused ? 1 : 0,
  };
}

// 1 is the most blocks in cleared lines; equal counts share a place, so
// two people on 12 behind someone on 20 are both 2nd.
export function placeOf(players, me) {
  const mine = players[me]?.cleared || 0;
  return 1 + players.filter((p) => (p.cleared || 0) > mine).length;
}

export function unpackWindow(snap) {
  const grid = [];
  for (let y = 0; y < snap.h; y += 1) {
    const row = [];
    for (let x = 0; x < snap.w; x += 1) {
      const ch = snap.g[y * snap.w + x];
      row.push(ch === '.' ? null : { owner: ch.charCodeAt(0) - 97 });
    }
    grid.push(row);
  }
  const players = Array.from({ length: snap.n }, (_, id) => ({
    id, piece: null, queue: [id === snap.me ? snap.q : null], lines: 0,
  }));
  snap.p.forEach(([id, shape, rot, x, y]) => { players[id].piece = { shape, rot, x, y }; });
  return {
    width: snap.w,
    height: snap.h,
    grid,
    players,
    x0: snap.x0,
    fullWidth: snap.W,
    me: snap.me,
    myCleared: snap.c || 0,
    myPlace: snap.r || 1,
    score: snap.s,
    lines: snap.l,
    level: snap.v,
    over: Boolean(snap.o),
    paused: Boolean(snap.z),
    events: [],
  };
}
